import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { fetchFarmInformation, updateFarmInformationWithLogo, getFarmLogoUrl, deleteFarmLogo } from '../supabase-client'
import { PageLayout } from './layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Upload, FileSpreadsheet } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '@/components/ui/alert-dialog'
import XeroIntegrationSettings from './xero/XeroIntegrationSettings'
import XeroCustomerMapping from './xero/XeroCustomerMapping'
import GoCardlessMandateMapping from './gocardless/GoCardlessMandateMapping'
import IntegrationActivityLog from './IntegrationActivityLog'

export default function FarmSettings({ user, onLogout }) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'general')
  const [farmInfo, setFarmInfo] = useState({
    business_name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    phone: '',
    email: '',
    company_number: '',
    opening_hours: '',
    bank_account_number: '',
    bank_sort_code: '',
    bank_name: '',
    bank_address: '',
    logo_file_path: '',
    logo_file_name: '',
    logo_uploaded_at: '',
    invoice_number_start: 1
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)

  useEffect(() => {
    loadFarmInformation()
  }, [])

  useEffect(() => {
    // Sync activeTab with URL parameter
    const tab = searchParams.get('tab')
    if (tab && tab !== activeTab) {
      setActiveTab(tab)
    }
  }, [searchParams])

  const handleTabChange = (value) => {
    setActiveTab(value)
    setSearchParams({ tab: value })
  }

  const loadFarmInformation = async () => {
    setLoading(true)
    try {
      const data = await fetchFarmInformation()
      if (data) {
        setFarmInfo(data)
        // Set current logo preview if exists
        if (data.logo_file_path) {
          const logoUrl = getFarmLogoUrl(data.logo_file_path)
          setLogoPreview(logoUrl)
        }
      }
    } catch {
      // If no farm info exists, that's okay - we'll create it on save
      console.log('No farm information found, will create new')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFarmInfo(prev => ({
      ...prev,
      [field]: value
    }))
    setSuccess(false)
    setError(null)
  }

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) {
      setLogoFile(null)
      setLogoPreview(farmInfo.logo_file_path ? getFarmLogoUrl(farmInfo.logo_file_path) : null)
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPG, PNG, GIF, or SVG file.')
      return
    }

    // Validate file size (2MB)
    const maxSize = 2 * 1024 * 1024
    if (file.size > maxSize) {
      setError('File too large. Please upload a file smaller than 2MB.')
      return
    }

    setLogoFile(file)
    setError(null)

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setLogoPreview(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  const handleDeleteLogo = async () => {
    if (!farmInfo.logo_file_path) return

    try {
      setLogoUploading(true)
      await deleteFarmLogo(farmInfo.logo_file_path)
      
      // Update farm info to remove logo references
      const updatedFarmInfo = {
        ...farmInfo,
        logo_file_path: '',
        logo_file_name: '',
        logo_uploaded_at: ''
      }
      
      await updateFarmInformationWithLogo(updatedFarmInfo)
      setFarmInfo(updatedFarmInfo)
      setLogoPreview(null)
      setLogoFile(null)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError('Failed to delete logo: ' + err.message)
    } finally {
      setLogoUploading(false)
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    
    try {
      const updatedFarmInfo = await updateFarmInformationWithLogo(farmInfo, logoFile)
      setFarmInfo(updatedFarmInfo)
      
      // Update logo preview if a new logo was uploaded
      if (logoFile && updatedFarmInfo.logo_file_path) {
        const logoUrl = getFarmLogoUrl(updatedFarmInfo.logo_file_path)
        setLogoPreview(logoUrl)
      }
      
      setLogoFile(null) // Clear selected file
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError('Failed to save farm information: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleImportData = () => {
    setShowImportDialog(true)
  }

  const handleImportConfirm = () => {
    setShowImportDialog(false)
    navigate('/import')
  }

  const handleImportCancel = () => {
    setShowImportDialog(false)
  }

  if (loading) {
    return (
      <PageLayout
        title="System Settings"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading farm settings...</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="System Settings"
      user={user}
      onLogout={onLogout}
    >
      <div className="h-full overflow-y-auto">
        <div className="mb-8 flex justify-end">
          <Button
            variant="outline"
            onClick={handleImportData}
            className="min-w-32"
          >
            Import Data
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription className="flex justify-between items-center">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
            </AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6">
            <AlertDescription className="flex justify-between items-center">
              Farm information saved successfully!
              <Button variant="ghost" size="sm" onClick={() => setSuccess(false)}>×</Button>
            </AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList>
            <TabsTrigger value="general">General Settings</TabsTrigger>
            <TabsTrigger value="integrations">Integrations</TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <form onSubmit={handleSave} className="space-y-6">
          
          {/* Business Information */}
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name *</Label>
                <Input
                  id="business_name"
                  value={farmInfo.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address_line_1">Address Line 1</Label>
                  <Input
                    id="address_line_1"
                    value={farmInfo.address_line_1}
                    onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address_line_2">Address Line 2</Label>
                  <Input
                    id="address_line_2"
                    value={farmInfo.address_line_2}
                    onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={farmInfo.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postcode">Postcode</Label>
                  <Input
                    id="postcode"
                    value={farmInfo.postcode}
                    onChange={(e) => handleInputChange('postcode', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={farmInfo.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    type="tel"
                    id="phone"
                    value={farmInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    type="email"
                    id="email"
                    value={farmInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_number">Company Number</Label>
                  <Input
                    id="company_number"
                    value={farmInfo.company_number}
                    onChange={(e) => handleInputChange('company_number', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="opening_hours">Opening Hours</Label>
                  <Input
                    id="opening_hours"
                    value={farmInfo.opening_hours}
                    onChange={(e) => handleInputChange('opening_hours', e.target.value)}
                    placeholder="e.g., Mon-Fri 9am-5pm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Information */}
          <Card>
            <CardHeader>
              <CardTitle>Bank Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={farmInfo.bank_name}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_account_number">Account Number</Label>
                  <Input
                    id="bank_account_number"
                    value={farmInfo.bank_account_number}
                    onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_sort_code">Sort Code</Label>
                  <Input
                    id="bank_sort_code"
                    value={farmInfo.bank_sort_code}
                    onChange={(e) => handleInputChange('bank_sort_code', e.target.value)}
                    placeholder="XX-XX-XX"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bank_address">Bank Address</Label>
                <Textarea
                  id="bank_address"
                  value={farmInfo.bank_address}
                  onChange={(e) => handleInputChange('bank_address', e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Data Import */}
          <Card>
            <CardHeader>
              <CardTitle>Data Import</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">Import Wizard V2</h3>
                      <p className="text-sm text-muted-foreground">
                        Import sales data from CSV files with intelligent learning
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    The Import Wizard learns from your data associations to accelerate future imports.
                    First imports are slow for learning, then dramatically faster.
                  </p>
                  <Button
                    onClick={() => navigate('/import-wizard')}
                    className="flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Start Import Wizard
                  </Button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">Expected CSV Format:</h4>
                  <code className="text-xs bg-white p-2 rounded block overflow-x-auto">
                    Tray Number, Date, Customer, Strain, Product Type, <br />
                    Price (£/Kg/Unit), Quantity (Kg/Units), Sub-Total (£), Invoice #
                  </code>
                  <p className="text-xs text-muted-foreground mt-2">
                    The wizard will help map your CSV columns to Odin's data structure.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="logo_file">Farm Logo</Label>
                  <Input
                    type="file"
                    id="logo_file"
                    accept="image/jpeg,image/png,image/gif,image/svg+xml"
                    onChange={handleLogoFileChange}
                    disabled={logoUploading || saving}
                    className="cursor-pointer"
                  />
                  <p className="text-sm text-muted-foreground">
                    Upload your farm logo (JPG, PNG, GIF, or SVG, max 2MB)
                  </p>
                  
                  {logoPreview && (
                    <div className="flex items-center gap-4 mt-4">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="max-w-[150px] max-h-[100px] border rounded-md p-2"
                      />
                      {farmInfo.logo_file_path && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleDeleteLogo}
                          disabled={logoUploading || saving}
                        >
                          {logoUploading ? 'Deleting...' : 'Delete Logo'}
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {logoFile && (
                    <p className="text-sm text-green-600 mt-2">
                      New logo selected: {logoFile.name}
                    </p>
                  )}
                </div>
            </CardContent>
          </Card>

              {/* Save Button */}
              <div className="flex justify-end pt-6">
                <Button
                  type="submit"
                  disabled={saving}
                  className="min-w-32"
                >
                  {saving ? 'Saving...' : 'Save Farm Settings'}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="integrations">
            <div className="space-y-6">
              {/* Xero Connection Settings */}
              <XeroIntegrationSettings user={user} />

              {/* Xero Customer Mapping Section */}
              <XeroCustomerMapping user={user} />

              <Separator className="my-8" />

              {/* GoCardless Mandate Mapping Section */}
              <GoCardlessMandateMapping user={user} />

              <Separator className="my-8" />

              {/* Integration Activity Log */}
              <IntegrationActivityLog user={user} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Import Data Confirmation Dialog */}
      <AlertDialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import Data</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to start importing data? This process will guide you through importing your CSV data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleImportCancel}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleImportCancel()
              }}
              autoFocus
            >
              Cancel (Esc)
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleImportConfirm}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleImportConfirm()
              }}
            >
              Continue (Ent)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  )
}
